import React, {useEffect, useState} from 'react';
import TemplateSelector from "./TemplateSelector";
import {EnvironmentTemplate} from "../types/EnvironmentTemplate";
import EnterDetails from "./EnterDetails";
import EnvironmentTemplateService from "../services/EnvironmentTemplateService";
import CustomerService from "../services/CustomerService";
import AppRolesService from "../../General/services/AppRolesService";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import {Alert, Spinner} from "@tag/tag-components-react-v4";
import styles from "../CustomerList/CustomerList.module.scss";

function CreateEnvironment() {
    const { accounts, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    while (!inProgress) {
        if (!AppRolesService.canUserCreateEnvironments(accounts, isAuthenticated)) {
            throw new Error()
        }
    }
    
    const [templates, setTemplates] = useState<EnvironmentTemplate[]>([]);
    const [loading, setLoading] = useState<string|boolean>(false)
    const [chosenTemplate, setChosenTemplate] = useState<EnvironmentTemplate>();
    const [requestSubmitted, setRequestSubmitted] = useState<boolean>(false);
    const [newClientId, setNewClientId] = useState<number>();
    const [errorMessage, setErrorMessage] = useState<string|undefined>();
    const [alert, setAlert] = useState<{type: any, message: string}>();

    const delay = (ms:number) => new Promise(res => setTimeout(res, ms));

    useEffect(() => {
        const templatesList: EnvironmentTemplate[] = [];
        EnvironmentTemplateService.getEnvironmentTemplates().then((templates: any) => {
            templates.forEach((t: any) => {
                templatesList.push({
                    type: t.type,
                    templateName: t.name,
                    templateServerName: t.templateServerName,
                    createOnServerName: t.appGroup,
                    companyNamingConvention: t.companyNamingConvention,
                    templateCustomerId: t.templateCustomerId,
                    templateCustomerName: t.templateCustomerName
                });
            });
            setTemplates(templatesList)
        })
    },[])
    
    const createEnvironment = async (companyName: string, countryId: number, templateType: number) => {
        setLoading('Submitting Request');
        setRequestSubmitted(true);
        let errorMessage = '';
        
        try {
            const requestCreateResponse = await CustomerService.requestCreateAppCustomer(companyName, countryId, templateType);

            const taskId = requestCreateResponse.taskId;

            let status;
            do {
                status = await CustomerService.createAppCustomerStatus(taskId);
                setLoading(status.progressMessage);
                await delay(50);
            } while (!status.complete && !status.errorMessage);

            if (status.errorMessage) {
                errorMessage = status.errorMessage;
                throw new Error();
            }

            setNewClientId(status.responseEntity.clientId);
            setAlert({type: 'success', message: 'Customer created successfully!'})
        } catch (e) {
            var genericError = 'An error has occured, please contact HSC DevOps'
            setAlert({type: 'error', message: errorMessage ?? genericError})
            setError(true, errorMessage ?? genericError);
        } finally {
            setLoading(false);
        }
    }

    const setError = (error: boolean, message?: string) => {
        if (!error) {
            setErrorMessage(undefined);
        }
        else {
            setErrorMessage(message);
        }
    }
    
    let content = null;
    if (errorMessage) {
        content = <h3 style={{color: 'red'}}>{errorMessage}</h3>
    }
    else if (loading) {
        content = <Spinner full>{loading ?? ''}</Spinner>
    }
    else if (!chosenTemplate) {
        content = (
            <TemplateSelector 
                templates={templates} 
                setChosenTemplate={setChosenTemplate}
            />
        );
    }
    else if (chosenTemplate && !requestSubmitted) {
        content = (
            <EnterDetails
                createEnvironmentFn={createEnvironment}
                chosenTemplate={chosenTemplate}
            />
        );
    }
    else if (requestSubmitted) {
        content = <h3>Client ID: {newClientId}</h3>
    }
    
    return (
        <>
            <h1>Create APP Environment</h1>
            {content}
            {alert ?
                <Alert
                    className={styles.CustomerListAlert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={1200}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }
        </>
    )

}

export default CreateEnvironment;
